import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const statusColors = {
  Active: "success",
  "Active ": "success",
  Suspended: "warning",
  "Not Managed": "default",
  "NOT Managed": "default",
  "NO INFO": "default",
  Canceling: "error",
  "Low Credit": "warning",
  Cancelled: "error",
  Cleared: "success",
  Pending: "info", 
};

// Helper function para makuha ang Abbreviation at Color base sa Bank Name
const getBankBadges = (bankString) => {
  if (!bankString) return [];
  
  const badges = [];
  const lower = bankString.toLowerCase();

  // Logic para sa detection ng banko
  if (lower.includes('american') || lower.includes('ax') || lower.includes('amex')) {
    badges.push('AMEX');
  }
  if (lower.includes('chase') || lower.includes('jpc')) {
    badges.push('JPC');
  }
  if (lower.includes('us bank') || lower.includes('usb')) {
    badges.push('USB');
  }
  if (lower.includes('citi') || lower.includes('costco')) {
    badges.push('CITI');
  }
  if (lower.includes('wells') || lower.includes('wf')) {
    badges.push('WF');
  }
  if (lower.includes('america') || lower.includes('boa')) {
    badges.push('BOA');
  }

  // Kung walang match, pwede nating ilagay ang 'OTHER' o i-skip na lang
  if (badges.length === 0 && bankString.length > 0) {
      // Optional: Pwede mong i-enable to kung gusto mo makita ung iba
      // badges.push('OTHER'); 
  }

  // Remove duplicates (Unique lang)
  return [...new Set(badges)];
};

export default function CreditCardItem({ card, onViewDetails }) {
  const colorKey = statusColors[card.status] || "default";
  
  // Kunin ang mga badges
  const bankBadges = getBankBadges(card.bank);

  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      sx={{
        height: 300, 
        display: "flex", 
        flexDirection: "column",
        borderRadius: 3, 
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)", 
        transition: "transform 0.2s, box-shadow 0.2s", 
        border: "1px solid rgba(0,0,0,0.08)",
        position: "relative",
        overflow: "visible", 
        "&:hover": {
          transform: "translateY(-4px)", 
          boxShadow: "0 10px 20px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Box
        sx={{
          height: 6,
          width: "100%",
          bgcolor: `${colorKey}.main`, 
        }}
      />

      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box sx={{ maxWidth: "70%" }}> 
            <Tooltip title={card.name} placement="top-start">
                <Typography variant="h6" fontWeight="bold" noWrap sx={{ lineHeight: 1.2, cursor: 'default' }}>
                {card.name}
                </Typography>
            </Tooltip>

            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontFamily: "monospace",
                letterSpacing: 0.5,
                bgcolor: "action.hover",
                px: 0.5,
                borderRadius: 1,
              }}
            >
              ID: {card.id}
            </Typography>
          </Box>
          <Chip
            label={card.status}
            color={colorKey}
            size="small"
            sx={{ fontWeight: 600, fontSize: "0.7rem", height: 24 }}
          />
        </Box>

        {/* Info Block (Updated: Bank Icons & Wrapped) */}
        <Box
          sx={{
            bgcolor: "background.default", 
            p: 2,
            borderRadius: 2,
            border: "1px dashed rgba(0,0,0,0.1)",
            mb: 2,
            minHeight: "60px", // Para consistent ang height kahit walang bank
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          }}
        >
           {/* BANK BADGES SECTION */}
           <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
              {bankBadges.length > 0 ? (
                <>
                  {(expanded ? bankBadges : bankBadges.slice(0, 3)).map((badge) => (
                    <Box
                      key={badge}
                      sx={{
                        bgcolor: "#e3f2fd", // Light Blue Background
                        color: "#1565c0",   // Darker Blue Text
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        border: "1px solid #bbdefb",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                      }}
                    >
                      {badge}
                    </Box>
                  ))}
                  <IconButton size="small" onClick={() => setExpanded(!expanded)} disabled={bankBadges.length <= 3} sx={{ p: 0.5 }}>
                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </>
              ) : (
                // Fallback kung walang detected na banko pero may data
                <Typography variant="caption" color="text.disabled" fontStyle="italic">
                   {card.bank ? "Unknown Bank" : "No Bank Data"}
                </Typography>
              )}
           </Box>

           {/* ACCOUNT COUNT (Optional: Nilagay ko dito sa baba ng badges, simple text lang) */}
           {card.cardType && (
             <Box mt={1}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {card.cardType} {/* E.g. "5 Accounts" */}
                </Typography>
             </Box>
           )}
        </Box>

        {/* Footer Info */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
                Last Payment
            </Typography>
            <Typography variant="body2" fontWeight="bold" color={card.lastPayment ? "text.primary" : "text.disabled"}>
                 {card.lastPayment || "No Record"}
            </Typography>
        </Box>

      </CardContent>

      <Divider />

      <Box sx={{ p: 2, pt: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            boxShadow: "none",
            bgcolor: "text.primary", 
            color: "background.paper",
            "&:hover": {
                bgcolor: "text.secondary",
                boxShadow: "none"
            }
          }}
          onClick={onViewDetails}
        >
          View Full Details
        </Button>
      </Box>
    </Card>
  );
}